import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { describe, type Problem } from "./problem";
import { currentRead, within } from "./read";

export type QueryState<T> =
  | { status: "idle"; data?: undefined }
  | { status: "loading"; data?: T }
  | { status: "ready"; data: T }
  | { status: "error"; problem: Problem; data?: T };

/** What a fetcher returns when there is nothing to ask — no site picked yet,
 * a panel not open. The query is then `idle`: no request is made and nothing
 * is shown for it. It is not an error, and it is never reported as one: a
 * Problem on screen carries a reason and a request id the server produced. */
export const SKIP: unique symbol = Symbol("skip");

type Result<T> = { data?: T; error?: unknown; response: Response };

/** How a read was asked for, handed to a fetcher. `unattended` is true for a
 * read no person asked for — a timer, or a re-read after the server announced
 * a change — and the fetcher then sends `Zenith-Unattended` (client.ts
 * `unattended`), so the read does not keep the session alive against its idle
 * bound (ADR-0192 decision 3b, ZENITH.md §19.2). Every other read is use. */
export type ReadHow = { unattended: boolean };

/** What `reload` takes to mark the read it starts as unattended. A string and
 * not a boolean, because `reload` is handed to `onClick` as it stands and a
 * click's event is truthy: only this exact value marks a read, so a person's
 * click is always use. */
export const UNATTENDED = "unattended" as const;

const sameDeps = (a: unknown[] | undefined, b: unknown[]) => a !== undefined && a.length === b.length && a.every((v, i) => Object.is(v, b[i]));

/** Runs an openapi-fetch call when `deps` change and exposes a `reload`.
 * The fetcher returns openapi-fetch's { data, error, response } shape, or
 * `SKIP` when it has nothing to ask.
 *
 * The previous answer stays on screen through a `reload` (no flash to empty
 * while the same rows are refetched) and is dropped when `deps` change: the
 * rows then belong to another key — another site's devices, another
 * customer's sites — and showing them under the new one, with their online
 * state and their action buttons, is showing the wrong scope.
 *
 * **Each run of the effect owns an `AbortController`, and a superseded run's
 * request is stopped, not only its answer ignored** (ADR-0419 decision 1,
 * `1d`, #1861). The run is superseded when its deps change, when a `reload`
 * starts a newer read, or when the component unmounts. Its requests are the
 * ones the fetcher starts while this hook calls it (`read.ts`), and two runs
 * — here or in any other component — asking for the identical request while
 * it is in flight share one (`client.ts`). **A stopped read is never an
 * error**: nothing is shown for it, and the `latest` counter still keeps any
 * stale answer off the screen. */
export function useQuery<T>(
  fetcher: (how: ReadHow) => Promise<Result<T>> | typeof SKIP,
  deps: unknown[],
): QueryState<T> & { reload: (mark?: unknown) => void } {
  const [state, setState] = useState<QueryState<T>>({ status: "loading" });
  const [tick, setTick] = useState(0);
  const latest = useRef(0);
  const lastDeps = useRef<unknown[] | undefined>(undefined);
  // Whether the read the next tick starts was asked for by nobody. Set by
  // `reload(UNATTENDED)`, read and cleared by the effect it triggers; a change
  // of deps is the page's own and is use.
  const nextUnattended = useRef(false);
  useEffect(() => {
    const id = ++latest.current;
    const rekeyed = !sameDeps(lastDeps.current, deps);
    lastDeps.current = deps;
    const how = { unattended: nextUnattended.current && !rekeyed };
    nextUnattended.current = false;
    const stop = new AbortController();
    const pending = within({ signal: stop.signal }, () => fetcher(how));
    // This effect *is* the fetch: the status it sets is the external system's.
    if (pending === SKIP) { setState({ status: "idle" }); return () => stop.abort(); }
    setState((s) => ({ status: "loading", data: rekeyed ? undefined : s.data }));
    pending
      .then(({ data, error, response }) => {
        if (id !== latest.current || stop.signal.aborted) return;
        if (data !== undefined && !error) setState({ status: "ready", data });
        else setState((s) => ({ status: "error", problem: describe(error, response.status), data: s.data }));
      })
      .catch(() => {
        if (id === latest.current && !stop.signal.aborted) setState((s) => ({ status: "error", problem: describe(undefined), data: s.data }));
      });
    return () => stop.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller's `deps` array is the key, by contract
  }, [...deps, tick]);
  const reload = useCallback((mark?: unknown) => {
    nextUnattended.current = mark === UNATTENDED;
    setTick((t) => t + 1);
  }, []);
  return { ...state, reload };
}

type PageOf<T> = { items: T[]; next_page_token?: string };

interface Extra<T> { key: string; pages: T[][]; token?: string; loading: boolean; problem?: Problem }

/** Pages through a collection. The first page is a `useQuery` on `deps`; the
 * pages after it are appended by `more()` and belong to the key those deps
 * make, so a change of key — a new filter, another scope — drops them with
 * the first page rather than leaving another key's rows under this one's.
 * A next page that fails is reported as `moreProblem`, the way the first
 * page's failure is `problem`; it used to be swallowed on the audit page
 * (#190). */
export function usePages<T>(
  fetcher: (token?: string) => Promise<Result<PageOf<T>>> | typeof SKIP,
  deps: unknown[],
): QueryState<PageOf<T>> & { reload: () => void; items: T[]; hasMore: boolean; loadingMore: boolean; moreProblem?: Problem; more: () => void } {
  const first = useQuery(() => fetcher(undefined), deps);
  const key = deps.map((d) => String(d)).join("\u0000");
  const [extra, setExtra] = useState<Extra<T>>({ key, pages: [], loading: false });
  // Another key's extra pages are not this key's: read as empty rather than
  // reset by an effect, so there is no render in between that shows them.
  const mine = extra.key === key ? extra : { key, pages: [], loading: false };
  const latest = useRef(0);
  const token = mine.pages.length > 0 ? mine.token : first.data?.next_page_token;
  const more = useCallback(() => {
    if (!token || mine.loading) return;
    const id = ++latest.current;
    setExtra({ ...mine, loading: true, problem: undefined });
    const pending = fetcher(token);
    if (pending === SKIP) { setExtra({ ...mine, loading: false }); return; }
    pending
      .then(({ data, error, response }) => {
        if (id !== latest.current) return;
        if (data !== undefined && !error) setExtra((e) => ({ key, pages: [...(e.key === key ? e.pages : []), data.items], token: data.next_page_token, loading: false }));
        else setExtra((e) => ({ ...(e.key === key ? e : { key, pages: [] }), loading: false, problem: describe(error, response.status) }));
      })
      .catch(() => {
        if (id === latest.current) setExtra((e) => ({ ...(e.key === key ? e : { key, pages: [] }), loading: false, problem: describe(undefined) }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `fetcher` is keyed by `deps`, as in useQuery
  }, [token, mine, key]);
  const items = useMemo(() => [...(first.data?.items ?? []), ...mine.pages.flat()], [first.data, mine.pages]);
  return { ...first, items, hasMore: !!token, loadingMore: mine.loading, moreProblem: mine.problem, more };
}

/** How much of the estate the rail loads to name a scope. A placeholder
 * standing in for ZENITH.md §29's tenancy-limits question — sites per
 * customer, customers per organization, that the scope walk must stay fast
 * for — which no one has measured; it is not a product number and moving it
 * is that question's answer, not a tuning (#188). Past it the rail says what
 * it did not load and offers to search for it: `GET /v1/search` finds a
 * customer or a site among everything the caller can read (#728), which is
 * what reaches a scope this bound left out. */
export const ESTATE_DRAIN = 200;

/** Drains a paginated collection up to `max` items; `more` says whether the
 * server had more than that. Used for counts and for the scope switcher's
 * name lookups, which must be live (ZENITH.md §18). */
export async function drain<T>(
  page: (token?: string) => Promise<{ data?: { items: T[]; next_page_token?: string }; error?: unknown; response: Response }>,
  max = ESTATE_DRAIN,
): Promise<{ items: T[]; more: boolean; problem?: Problem }> {
  // A drain a `useQuery` fetcher starts asks every page as that run's read,
  // not only the first (`read.ts`).
  const read = currentRead();
  const items: T[] = [];
  let token: string | undefined;
  do {
    const { data, error, response } = await within(read, () => page(token));
    if (!data || error) return { items, more: false, problem: describe(error, response.status) };
    items.push(...data.items);
    token = data.next_page_token;
    if (items.length >= max && token) return { items, more: true };
  } while (token);
  return { items, more: false };
}

/** A collection read to its end, as one query (#1259).
 *
 * **Every page is asked for, following `next_page_token` until the server
 * gives none.** There is no item bound of its own: the page size is the
 * server's (`PAGE_SIZE_MAX`, ZENITH.md §29, ruled by ADR-0072), and how many
 * devices a site holds is §29's tenancy-limits question, answered nowhere — so
 * the list is read whole, the way a site's plan is. This is not `drain` above,
 * whose `ESTATE_DRAIN` is the estate rail's placeholder and not a device bound.
 *
 * The answer is the **first** page's envelope (`cell_id`, `venue_id`, …) with
 * every page's `items`, and no `next_page_token`, because nothing is left
 * unread. `combine` folds a later page's envelope into it, for an envelope
 * that is per page rather than per collection — a customer read's `sites`.
 *
 * **A later page that fails does not make the list fail, and does not make it
 * whole.** The rows already read are kept and `incomplete` carries the
 * page's Problem, so a page shows them beside the error and says the list is
 * short. A short list presented as the whole one is the defect this exists to
 * end, and a list thrown away because page three failed would be the same
 * defect in the other direction. A first page that fails is the query's
 * error, exactly as `useQuery` reports one. */
export type Drained<P> = P & { incomplete?: Problem };

export function useDrained<T, P extends PageOf<T>>(
  fetcher: (token?: string) => Promise<Result<P>> | typeof SKIP,
  deps: unknown[],
  combine?: (acc: P, next: P) => P,
): QueryState<Drained<P>> & { reload: () => void } {
  return useQuery<Drained<P>>(() => {
    // Every page is this run's read, the ones after the first included: they
    // are asked after an `await`, so the run is carried to them (`read.ts`).
    const read = currentRead();
    const first = fetcher(undefined);
    if (first === SKIP) return SKIP;
    return first.then(async (r): Promise<Result<Drained<P>>> => {
      if (r.data === undefined || r.error) return { error: r.error, response: r.response };
      let acc: P = r.data;
      const items: T[] = [...r.data.items];
      let token = r.data.next_page_token;
      while (token) {
        const pending = within(read, () => fetcher(token));
        if (pending === SKIP) break;
        let next: Result<P>;
        try { next = await pending; } catch { return { data: { ...acc, items, next_page_token: undefined, incomplete: describe(undefined) }, response: r.response }; }
        if (next.data === undefined || next.error) {
          return { data: { ...acc, items, next_page_token: undefined, incomplete: describe(next.error, next.response.status) }, response: next.response };
        }
        items.push(...next.data.items);
        if (combine) acc = combine(acc, next.data);
        // A cursor that does not move would be asked for forever. It is a
        // server defect, not a bound, so it ends the read as incomplete.
        if (next.data.next_page_token === token) {
          return { data: { ...acc, items, next_page_token: undefined, incomplete: { reason: "internal", message: "The server answered the same page token twice, so the rest of the list was not read.", request_id: "" } }, response: next.response };
        }
        token = next.data.next_page_token;
      }
      return { data: { ...acc, items, next_page_token: undefined }, response: r.response };
    });
  }, deps);
}
